import type { Tier } from '../types'

// Tier color map. T1 emerald (top), T2 sky, T3 amber, T4 red (bottom).
// Same palette as LeaderboardPage so the two surfaces stay visually consistent.
const TIER_BADGE_CLASS: Record<Tier, string> = {
  1: 'bg-emerald-900/60 text-emerald-200 border-emerald-700',
  2: 'bg-sky-900/60 text-sky-200 border-sky-700',
  3: 'bg-amber-900/60 text-amber-200 border-amber-700',
  4: 'bg-red-900/60 text-red-200 border-red-700',
}

export interface TierBadgeProps {
  tier: Tier | null | undefined
  tierName?: string | null
  /**
   * Gate failures appended to the tooltip — handy on table rows where the
   * gate-failures section isn't rendered separately. Optional.
   */
  gateFailures?: string[] | null
  /**
   * "compact" → just `T{n}` (table cells, leaderboard rows).
   * "labeled" → `T{n} <tier_name>` inline (page headers).
   * Default: "labeled".
   */
  variant?: 'compact' | 'labeled'
  className?: string
}

export function TierBadge({
  tier,
  tierName,
  gateFailures,
  variant = 'labeled',
  className,
}: TierBadgeProps) {
  if (tier === null || tier === undefined) {
    return <span className="text-xs text-slate-600">--</span>
  }

  const cls = TIER_BADGE_CLASS[tier]
  const titleParts: string[] = []
  if (tierName) titleParts.push(`Tier ${tier} - ${tierName}`)
  if (gateFailures && gateFailures.length > 0) {
    titleParts.push(gateFailures.join('\n'))
  }
  const title = titleParts.join('\n') || undefined

  if (variant === 'compact') {
    return (
      <span
        data-tier={tier}
        title={title}
        className={`inline-block rounded border px-1.5 py-0.5 font-mono text-xs ${cls}${className ? ` ${className}` : ''}`}
      >
        T{tier}
      </span>
    )
  }

  return (
    <span
      data-tier={tier}
      title={title}
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium ${cls}${className ? ` ${className}` : ''}`}
    >
      <span className="font-mono">T{tier}</span>
      {tierName && <span className="opacity-80">{tierName}</span>}
    </span>
  )
}
